import { toaster } from 'components/ui/toaster'

/**
 * After a deployment, code-split JS chunks get new content hashes. Users with a
 * cached index.html still reference the old chunk URLs. The CDN/server's SPA
 * fallback returns index.html (text/html) instead of the JS file, causing:
 *
 *   TypeError: 'text/html' is not a valid JavaScript MIME type.
 *
 * Vite fires a `vite:preloadError` event when a dynamic-import preload fails.
 * We listen for it and show a non-intrusive toast prompting the user to refresh,
 * rather than auto-reloading (which could interrupt form entry or other work).
 */

let toastShown = false

function showVersionToast(): void {
  if (toastShown) return
  toastShown = true

  toaster.create({
    title: 'New version available',
    description: 'Refresh the page to get the latest updates.',
    type: 'info',
    action: {
      label: 'Refresh',
      onClick: () => window.location.reload(),
    },
    closable: true,
    duration: Infinity,
  })
}

/**
 * Register a listener for Vite's `vite:preloadError` event. This fires when
 * a dynamic import preload fails (e.g., stale chunk URL). We show a toast but
 * do NOT call preventDefault: that makes Vite resolve the import to
 * `undefined`, the router then crashes on `undefined.component`, and the user
 * gets the generic "Something went wrong" instead of the stale-chunk card in
 * DefaultErrorComponent.
 */
export function handleStaleChunkPreloadError(): void {
  window.addEventListener('vite:preloadError', () => {
    showVersionToast()
  })
}

/** Expose showVersionToast for use by version polling and error components. */
export { showVersionToast }

/** Returns true if the error looks like a stale-chunk / MIME-type failure. */
export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message
  return (
    msg.includes('is not a valid JavaScript MIME type') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  )
}
